import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Radio, CalendarClock } from "lucide-react";
import { api } from "@/lib/api";
import { PeepingLogo } from "@/components/PeepingLogo";

function fmt(iso) {
  if (!iso) return "Time to be announced";
  try {
    return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}

export default function Live() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/live/status").then((r) => setData(r.data)).catch(() => {});
  }, []);

  const isLive = data?.state === "live";
  const next = data?.next;

  return (
    <div data-testid="live-screen" className="relative min-h-screen">
      <div className="relative h-[48vh] min-h-[340px] overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#2a0f3d] via-[#120818] to-[#050407]" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#080808]/40 via-transparent to-[#080808]" />

        <header className="relative z-10 flex items-center justify-between px-5 pt-6">
          <PeepingLogo className="w-28 items-start" showTagline={false} />
        </header>

        <div className="absolute left-5 top-20 z-10 flex items-center gap-2">
          <span
            data-testid="live-state-badge"
            className={`flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-bold uppercase ${
              isLive ? "bg-[#d42be8]" : "bg-white/10 text-[#a7a0af]"
            }`}
          >
            <Radio size={12} /> {isLive ? "Live" : "Offline"}
          </span>
        </div>

        <div className="absolute bottom-5 left-5 right-5 z-10">
          <h1 className="text-2xl font-extrabold">{isLive ? data?.live_now?.title : "No creators are live right now."}</h1>
          <p className="mt-1 text-sm text-white/80">{data?.note}</p>
        </div>
      </div>

      <div className="px-5 pt-5">
        {(data?.live || []).length > 0 && (
          <div data-testid="live-now-list" className="mb-4 flex flex-col gap-2">
            {data.live.map((s) => (
              <button key={s.id} onClick={() => navigate(`/app/live/${s.id}`)} data-testid={`live-watch-${s.id}`} className="flex items-center justify-between gap-3 rounded-2xl border border-[#d42be8]/50 bg-[#d42be8]/10 p-4 text-left">
                <span className="min-w-0">
                  <span className="block truncate font-bold">{s.title}</span>
                  <span className="block text-xs text-[#f0a8fa]">{s.creator?.display_name} · {s.locked ? "Members only" : "Live now"}</span>
                </span>
                <span className="btn-brand flex shrink-0 items-center gap-1 rounded-full px-4 py-2 text-xs font-semibold"><Radio size={13} /> {s.locked ? "Unlock" : "Watch"}</span>
              </button>
            ))}
          </div>
        )}
        {/* Next scheduled */}
        {next && (
          <div data-testid="live-next" className="rounded-2xl glass p-4">
            <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#d42be8]">
              <CalendarClock size={14} /> Next session
            </p>
            <h2 className="mt-2 text-lg font-bold">{next.title}</h2>
            {next.description && <p className="mt-1 text-sm text-[#a7a0af]">{next.description}</p>}
            <p className="mt-2 text-xs text-white/80">{fmt(next.scheduled_for)}</p>
          </div>
        )}

        {/* Upcoming list */}
        <div className="mt-5">
          <h3 className="text-sm font-bold uppercase tracking-wider text-[#a7a0af]">Upcoming</h3>
          {(data?.upcoming || []).length === 0 ? (
            <div data-testid="live-empty" className="mt-3 rounded-2xl glass p-6 text-center text-sm text-[#a7a0af]">
              No upcoming sessions scheduled yet.
            </div>
          ) : (
            <ul className="mt-3 flex flex-col gap-2">
              {data.upcoming.map((s) => (
                <li key={s.id} data-testid={`live-upcoming-${s.id}`} className="flex items-center gap-3 rounded-2xl glass p-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-[#6a00ff]/40 to-[#d42be8]/30">
                    <Radio size={16} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold">{s.title}</p>
                    <p className="truncate text-xs text-[#a7a0af]">{fmt(s.scheduled_for)}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <p className="mt-4 text-center text-[0.65rem] text-[#6f6878]">
          Live chat opens inside each broadcast.
        </p>
      </div>
    </div>
  );
}
